import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import FormField from '../common/FormField';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { EmailTemplate } from '../../types/email';
import { adminService } from '../../services/admin';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const ComposerCard = styled(Card)`
  padding: ${spacing[6]};
`;

const ComposerTitle = styled.h3`
  margin: 0 0 ${spacing[4]} 0;
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.display};
`;

const TemplateSection = styled.div`
  margin-bottom: ${spacing[4]};
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
`;

const VariablesSection = styled.div`
  margin-bottom: ${spacing[4]};
`;

const VariablesTitle = styled.h4`
  margin: 0 0 ${spacing[3]} 0;
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const VariablesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const VariableButton = styled(Button)`
  font-size: ${typography.fontSize.xs};
  padding: ${spacing[2]} ${spacing[3]};
`;

const EditorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const EditorTabs = styled.div`
  display: flex;
  gap: ${spacing[2]};
  border-bottom: ${borders.width.base} solid ${props => props.theme.colors.border};
`;

const Tab = styled.button<{ active: boolean }>`
  padding: ${spacing[3]} ${spacing[4]};
  border: none;
  background: none;
  border-bottom: ${props => props.active ? `2px solid ${props.theme.colors.primary}` : '2px solid transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  font-weight: ${props => props.active ? typography.fontWeight.semibold : typography.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const EditorTextarea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  font-family: 'Courier New', monospace;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  resize: vertical;
  line-height: ${typography.lineHeight.relaxed};
`;

const PreviewSection = styled.div`
  padding: ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.surface};
  min-height: 300px;
`;

const PreviewContent = styled.div`
  font-size: ${typography.fontSize.sm};
  line-height: ${typography.lineHeight.relaxed};
  color: ${props => props.theme.colors.text};
  
  h1, h2, h3 {
    margin-top: ${spacing[4]};
    margin-bottom: ${spacing[2]};
  }
  
  p {
    margin-bottom: ${spacing[3]};
  }
`;

const LanguageHint = styled.p`
  margin: 0 0 ${spacing[3]} 0;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

type ContentLanguage = 'gr' | 'en';
type EditorMode = 'edit' | 'preview';

interface EmailComposerProps {
  subject: string;
  body: string;
  subjectEn: string;
  bodyEn: string;
  onSubjectChange: (subject: string) => void;
  onBodyChange: (body: string) => void;
  onSubjectEnChange: (subject: string) => void;
  onBodyEnChange: (body: string) => void;
  templateId?: string;
  onTemplateChange?: (templateId: string) => void;
  /** Force active language tab (e.g. when English content is missing). */
  forceLanguage?: ContentLanguage | null;
  fieldErrors?: {
    subject?: string;
    body?: string;
    subjectEn?: string;
    bodyEn?: string;
  };
}

export const EmailComposer: React.FC<EmailComposerProps> = ({
  subject,
  body,
  subjectEn,
  bodyEn,
  onSubjectChange,
  onBodyChange,
  onSubjectEnChange,
  onBodyEnChange,
  templateId,
  onTemplateChange,
  forceLanguage = null,
  fieldErrors,
}) => {
  const { t } = useTranslation();
  const availableVariables = [
    { key: 'FirstName', label: t('common.firstName') },
    { key: 'LastName', label: t('common.lastName') },
    { key: 'FullName', label: t('common.fullName') },
    { key: 'Email', label: t('common.email') },
  ];
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(templateId || '');
  const [contentLanguage, setContentLanguage] = useState<ContentLanguage>('gr');
  const [editorMode, setEditorMode] = useState<EditorMode>('edit');

  useEffect(() => {
    if (forceLanguage) {
      setContentLanguage(forceLanguage);
    }
  }, [forceLanguage]);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        onSubjectChange(template.subject);
        onBodyChange(template.body);
        onSubjectEnChange(template.subjectEn || '');
        onBodyEnChange(template.bodyEn || '');
        if (onTemplateChange) {
          onTemplateChange(template.id);
        }
      }
    }
  }, [selectedTemplateId, templates]);

  const loadTemplates = async () => {
    try {
      const loadedTemplates = await adminService.getEmailTemplates();
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const currentSubject = contentLanguage === 'gr' ? subject : subjectEn;
  const currentBody = contentLanguage === 'gr' ? body : bodyEn;
  const setCurrentSubject = contentLanguage === 'gr' ? onSubjectChange : onSubjectEnChange;
  const setCurrentBody = contentLanguage === 'gr' ? onBodyChange : onBodyEnChange;
  const bodyFieldName = contentLanguage === 'gr' ? 'body' : 'bodyEn';

  const insertVariable = (variable: string) => {
    const variablePlaceholder = `{{${variable}}}`;
    const textarea = document.querySelector(`textarea[name="${bodyFieldName}"]`) as HTMLTextAreaElement | null;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + variablePlaceholder + text.substring(end);
      setCurrentBody(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variablePlaceholder.length, start + variablePlaceholder.length);
      }, 0);
    } else {
      setCurrentBody(currentBody + variablePlaceholder);
    }
  };

  const getPreviewContent = () => {
    let preview = currentBody;
    const sampleFirst = contentLanguage === 'gr' ? 'Γιάννης' : 'John';
    const sampleLast = contentLanguage === 'gr' ? 'Παπαδόπουλος' : 'Doe';
    preview = preview.replace(/{{FirstName}}/g, sampleFirst);
    preview = preview.replace(/{{LastName}}/g, sampleLast);
    preview = preview.replace(/{{FullName}}/g, `${sampleFirst} ${sampleLast}`);
    preview = preview.replace(/{{Email}}/g, 'john.doe@example.com');
    return preview;
  };

  return (
    <Container>
      <ComposerCard variant="elevated">
        <ComposerTitle>{t('email.composeEmail')}</ComposerTitle>
        <LanguageHint>{t('email.bilingualHint')}</LanguageHint>

        {templates.length > 0 && (
          <TemplateSection>
            <FormField
              label={t('email.selectTemplate')}
              select
              value={selectedTemplateId}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value);
                if (onTemplateChange) {
                  onTemplateChange(e.target.value);
                }
              }}
              options={[
                { value: '', label: t('email.noTemplate') },
                ...templates.map(tmpl => ({ value: tmpl.id, label: tmpl.name })),
              ]}
            />
          </TemplateSection>
        )}

        <VariablesSection>
          <VariablesTitle>{t('email.insertVariables')}</VariablesTitle>
          <VariablesGrid>
            {availableVariables.map((variable) => (
              <VariableButton
                key={variable.key}
                variant="outline"
                size="sm"
                onClick={() => insertVariable(variable.key)}
              >
                {`{{${variable.key}}}`}
              </VariableButton>
            ))}
          </VariablesGrid>
        </VariablesSection>

        <EditorSection>
          <EditorTabs>
            <Tab active={contentLanguage === 'gr'} onClick={() => setContentLanguage('gr')}>
              {t('email.languageGreek')}
            </Tab>
            <Tab active={contentLanguage === 'en'} onClick={() => setContentLanguage('en')}>
              {t('email.languageEnglish')}
            </Tab>
          </EditorTabs>

          <FormField
            label={`${t('email.subject')} (${contentLanguage === 'gr' ? t('email.languageGreek') : t('email.languageEnglish')})`}
            name={contentLanguage === 'gr' ? 'subject' : 'subjectEn'}
            value={currentSubject}
            onChange={(e) => setCurrentSubject(e.target.value)}
            required
            error={
              contentLanguage === 'gr'
                ? fieldErrors?.subject || fieldErrors?.body
                : fieldErrors?.subjectEn || fieldErrors?.bodyEn
            }
          />

          <EditorTabs>
            <Tab active={editorMode === 'edit'} onClick={() => setEditorMode('edit')}>
              {t('email.edit')}
            </Tab>
            <Tab active={editorMode === 'preview'} onClick={() => setEditorMode('preview')}>
              {t('email.preview')}
            </Tab>
          </EditorTabs>

          {editorMode === 'edit' ? (
            <EditorTextarea
              name={bodyFieldName}
              value={currentBody}
              onChange={(e) => setCurrentBody(e.target.value)}
              placeholder={t('email.bodyPlaceholder')}
            />
          ) : (
            <PreviewSection>
              <PreviewContent
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(getPreviewContent()),
                }}
              />
            </PreviewSection>
          )}
        </EditorSection>
      </ComposerCard>
    </Container>
  );
};

export default EmailComposer;
